/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext } from 'react';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { Faq } from 'ui/atoms/Faq/Faq';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { ConnectorChatCard } from 'ui/molecules/ConnectorChatCard/ConnectorChatCard';
import { EditorialStepsSection } from 'ui/molecules/EditorialStepsSection/EditorialStepsSection';
import { NetworkScreeningCard } from 'ui/molecules/NetworkScreeningCard/NetworkScreeningCard';
import { OurCustomersV2 } from 'ui/molecules/OurCustomers/OurCustomersV2';
import { OwnershipGraphCard } from 'ui/molecules/OwnershipGraphCard/OwnershipGraphCard';
import { ProcessSteps } from 'ui/molecules/ProcessSteps/ProcessSteps';
import { Section } from 'ui/molecules/Section/Section';
import { StatsBlock } from 'ui/molecules/StatsBlock/StatsBlock';
import { CtaSection } from 'ui/organisms/CtaSection/CtaSection';
import { NumberedFeatureCards } from 'ui/organisms/NumberedFeatureCards/NumberedFeatureCards';

import styles from './UboDiscoveryPage.module.scss';

import {
  getConnectorPoints,
  getEditorialRows,
  getFaqItems,
  getProcessSteps,
  getScreeningPoints,
  getStatlineItems,
  getUseCases,
} from './UboDiscoveryPage.data';

const Checklist = ({ points }: { points: string[] }) => (
  <ul className={styles.checklist}>
    {points.map((point) => (
      <li key={point}>{point}</li>
    ))}
  </ul>
);

export const UboDiscoveryPage = () => {
  const { t } = useInt();
  const { secondaryNavigation } = useContext(ContentContext);

  const freeTrialSlug = secondaryNavigation?.free_trial?.slug;
  const bookDemoSlug = secondaryNavigation?.book_demo?.slug;

  const editorialRows = getEditorialRows(t);
  const statlineItems = getStatlineItems(t);
  const processSteps = getProcessSteps(t);
  const useCases = getUseCases(t);
  const screeningPoints = getScreeningPoints(t);
  const connectorPoints = getConnectorPoints(t);
  const faqItems = getFaqItems(t);

  return (
    <div className={styles.wrapper}>
      <header className={styles.hero}>
        <div className={styles.heroInner}>
          <TitleBlock
            size="page"
            title={t('Search any person. See every company they’re connected to.')}
            description={
              <>
                {t('Directorships, shareholdings and beneficial ownership — pulled from')}{' '}
                <strong>{t('400+ government registries')}</strong> {t('across')}{' '}
                <strong>{t('200+ countries')}</strong>, {t('every connection cited to its source filing.')}
              </>
            }
          />

          {freeTrialSlug && (
            <div className={styles.heroCta}>
              <Button type="blue" size="xxl" route={freeTrialSlug}>
                {t('Trace an ownership chain')}
              </Button>
            </div>
          )}

          <OwnershipGraphCard className={styles.heroCard} />
        </div>
      </header>

      <OurCustomersV2 hasBackground={false} />

      <EditorialStepsSection
        background="blue"
        title={t('Ownership you can defend to a regulator.')}
        description={t(
          'Modeled ownership data collapses under audit. Every entity, shareholder and percentage in the graph is pulled from the official registry of record.',
        )}
        steps={editorialRows}
        bottomContent={<StatsBlock features={statlineItems} hasPadding={false} className={styles.statline} />}
      />

      <Section
        align="left"
        title={t('From a name to the full network in seconds.')}
        description={t('One query resolves the entire chain — in the platform, through Regis, or over the API.')}
      >
        <ProcessSteps steps={processSteps} className={styles.process} />
      </Section>

      <NumberedFeatureCards
        title={t('Built for the teams who carry the risk.')}
        description={t(
          'Ownership questions land on compliance, risk and data teams first. The graph answers them with registry evidence, not estimates.',
        )}
        columns={3}
        items={useCases.map((useCase, index) => ({
          num: `0${index + 1}`,
          tag: useCase.tag,
          title: useCase.title,
          description: useCase.description,
        }))}
      />

      <Section
        layout="split"
        title={t('Screen the whole network. In one pass.')}
        description={t(
          'Every company and affiliated individual in the graph — directors, shareholders, UBOs — checked against global sanctions and PEP lists in a single run. Risk hiding two layers deep in a shell structure surfaces immediately, with the registry evidence attached.',
        )}
        actions={
          <div className={styles.introExtras}>
            <Checklist points={screeningPoints} />
            {freeTrialSlug && (
              <Button type="blue" size="xl" route={freeTrialSlug}>
                {t('See screening in action')}
              </Button>
            )}
          </div>
        }
      >
        <NetworkScreeningCard />
      </Section>

      <Section
        layout="split"
        title={t('The ownership graph, inside Claude and ChatGPT.')}
        description={t(
          'Install the free Global Database connector and query ownership data from the assistant your team already uses — every answer cited to its registry. Prefer code? The same data is one REST call away.',
        )}
        actions={
          <div className={styles.introExtras}>
            <Checklist points={connectorPoints} />
            <div className={styles.connectorCtas}>
              {freeTrialSlug && (
                <Button type="dark" size="xl" route={freeTrialSlug}>
                  ✳ {t('Add to Claude — free')}
                </Button>
              )}
              {freeTrialSlug && (
                <Button type="dark-outline" size="xl" route={freeTrialSlug}>
                  ◈ {t('Add to ChatGPT — free')}
                </Button>
              )}
            </div>
          </div>
        }
      >
        <ConnectorChatCard />
      </Section>

      <CtaSection
        theme="dark"
        glow
        title={t('See who really owns your counterparties.')}
        description={t(
          'Trace a live ownership chain on a company you know — or talk to our team about API access and bulk delivery.',
        )}
        actions={
          <div className={styles.finalCtas}>
            {freeTrialSlug && (
              <Button type="white" size="xxl" route={freeTrialSlug}>
                {t('Trace an ownership chain')}
              </Button>
            )}

            {bookDemoSlug && (
              <Button type="white-outline" size="xxl" route={bookDemoSlug}>
                {t('Request a demo')}
              </Button>
            )}
          </div>
        }
      />

      <Faq title={t('Frequently asked questions.')} data={faqItems} />
    </div>
  );
};
